import { getFileExtension } from '@repo/lib/fs/getFileExtension';
import { DownloadIcon } from 'lucide-react';
import { useMemo } from 'react';

import type { MediaViewerFile } from '@/components/features/media/MediaViewer';

import { Button } from '@/components/ui/button';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { EmptyState, EmptyStateCircles, EmptyStateContent, EmptyStateDescription, EmptyStateFooter, EmptyStateTitle } from '@/components/ui/empty-state';
import { FileIcon } from '@/components/ui/file-icon';
import { getFileUrl } from '@/utils/file';

export default function MediaFileViewer(mediaFileViewerProps: MediaViewerFile) {
  const { open, onOpenChange, file } = mediaFileViewerProps;

  const [isFileObject, fileURL] = useMemo<[boolean, string]>(() => {
    if ('id' in file) {
      return [false, getFileUrl(file.id)];
    }
    if (file instanceof File) {
      return [true, URL.createObjectURL(file)];
    }
    return [false, ''];
  }, [file]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={'h-fit'}>
        <DialogBody>
          <DialogHeader>
            <DialogTitle>{file.name}</DialogTitle>
            <DialogDescription className={'sr-only'}>{file.name} - podgląd</DialogDescription>
          </DialogHeader>
          <div className={'flex h-full w-full flex-col items-center justify-center gap-3 p-4'}>
            <MediaFileViewerContent fileName={file.name} src={fileURL} isFileObject={isFileObject} />
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

interface MediaFileViewwerContentProps {
  isFileObject?: boolean;
  src: string;
  fileName: string;
}

export function MediaFileViewerContent(mediaFileViewwerContentProps: MediaFileViewwerContentProps) {
  const { isFileObject, src, fileName } = mediaFileViewwerContentProps;

  return (
    <EmptyState className="pb-12">
      <EmptyStateCircles className={'z-0'} />
      <FileIcon size={'lg'} fileExtension={getFileExtension(fileName)} />
      <EmptyStateContent>
        <EmptyStateTitle className="text-lg!">Podgląd niedostępny</EmptyStateTitle>
        <EmptyStateDescription className="text-sm">Nie można wyświetlić podglądu tego typu pliku.</EmptyStateDescription>
      </EmptyStateContent>
      <EmptyStateFooter>
        {!isFileObject && (
          <Button variant={'secondary'} asChild className={'w-full max-w-44'}>
            <a href={src} download target={'_self'}>
              <DownloadIcon />
              Pobierz
            </a>
          </Button>
        )}
      </EmptyStateFooter>
    </EmptyState>
  );
}
